import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useFrontChatBoot } from 'front-chat-sdk/hooks';
import { useAuthStore } from '@/store/authStore';
import { CHAT_ID, getFrontChatUserHash } from '@/lib/api/chat/frontChat';

interface FrontChatContextValue {
  unreadCount: number;
  isReady: boolean;
  openChat: () => void;
  hideChat: () => void;
}

const FrontChatContext = createContext<FrontChatContextValue>({
  unreadCount: 0,
  isReady: false,
  openChat: () => {},
  hideChat: () => {},
});

export function useFrontChat() {
  return useContext(FrontChatContext);
}

interface FrontChatProviderProps {
  children: ReactNode;
}

export function FrontChatProvider({ children }: FrontChatProviderProps) {
  const auth = useAuthStore();
  const [unreadCount] = useState(0);
  const [userHash, setUserHash] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const { frontChat, initialize, isInitialized } = useFrontChatBoot(document.body);

  // Fetch user hash when authenticated
  useEffect(() => {
    if (!auth.user?.email || !CHAT_ID) return;

    let cancelled = false;

    const fetchUserHash = async () => {
      try {
        const hashResponse = await getFrontChatUserHash();
        if (!cancelled) {
          setUserHash(hashResponse.userHash);
          setUserEmail(hashResponse.email);
        }
      } catch {
        // Failed to get user hash
      }
    };

    fetchUserHash();

    return () => {
      cancelled = true;
    };
  }, [auth.user?.email]);

  // Initialize FrontChat via SDK when we have the user hash
  useEffect(() => {
    if (!initialize || isInitialized || !userHash || !userEmail || !CHAT_ID) {
      return;
    }

    initialize({
      chatId: CHAT_ID,
      email: userEmail,
      userHash: userHash,
      useDefaultLauncher: false,
      shouldExpandOnShowWindow: false,
      shouldHideExpandButton: true,
      name: `${auth.user?.firstName} ${auth.user?.lastName}`,
      customFields: {
        'Organization': auth.tenant?.tenantName || 'Unknown',
        'User ID': auth.user?.id || '',
      },
    });
  }, [initialize, isInitialized, userHash, userEmail, auth.user, auth.tenant]);

  const openChat = useCallback(() => {
    if (frontChat && isInitialized) {
      frontChat('show');
    }
  }, [frontChat, isInitialized]);

  const hideChat = useCallback(() => {
    if (frontChat && isInitialized) {
      frontChat('hide');
    }
  }, [frontChat, isInitialized]);

  return (
    <FrontChatContext.Provider value={{ unreadCount, isReady: isInitialized, openChat, hideChat }}>
      {children}
    </FrontChatContext.Provider>
  );
}
